import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Clock, CheckCircle, XCircle, CaretDown } from "@phosphor-icons/react";

/**
 * Configuration of the 3 appointment status values.
 * Kept in a single object so ROW colours, chip colours, and dropdown are 100% consistent.
 */
export const STATUS_META = {
  pending: {
    label: "Pendiente",
    dot: "#FACC15",
    text: "#FACC15",
    bg: "rgba(250, 204, 21, 0.10)",
    border: "#FACC15",
    icon: Clock,
  },
  approved: {
    label: "Aprobada",
    dot: "#10B981",
    text: "#10B981",
    bg: "rgba(16, 185, 129, 0.10)",
    border: "#10B981",
    icon: CheckCircle,
  },
  cancelled: {
    label: "Cancelada",
    dot: "#FF3B30",
    text: "#FF3B30",
    bg: "rgba(255, 59, 48, 0.10)",
    border: "#FF3B30",
    icon: XCircle,
  },
};

export function normalizeStatus(s) {
  const v = (s || "").toLowerCase();
  if (v in STATUS_META) return v;
  return "pending";
}

const MENU_W = 160;
const MENU_H_APPROX = 108;

/**
 * Click-to-change status chip. Uses `createPortal` so the menu escapes the
 * table's stacking context and never gets covered by other rows.
 */
export function StatusChip({ status, onChange, disabled, testId }) {
  const s = normalizeStatus(status);
  const meta = STATUS_META[s];
  const Icon = meta.icon;
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const computePosition = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    // Open upwards if there's not enough room below
    const top = spaceBelow < MENU_H_APPROX + 8 ? r.top - MENU_H_APPROX - 4 : r.bottom + 4;
    const left = Math.min(
      Math.max(8, r.right - MENU_W),
      window.innerWidth - MENU_W - 8
    );
    setPos({ top, left });
  };

  useEffect(() => {
    if (!open) return;
    computePosition();
    const onDoc = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onWinChange = () => setOpen(false);
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("resize", onWinChange);
    window.addEventListener("scroll", onWinChange, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("resize", onWinChange);
      window.removeEventListener("scroll", onWinChange, true);
    };
  }, [open]);

  const pick = (next) => {
    setOpen(false);
    if (next === s) return;
    onChange?.(next);
  };

  return (
    <>
      <button
        ref={btnRef}
        data-testid={testId}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen((v) => !v);
        }}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm font-mono-data text-[10px] uppercase tracking-widest border transition-colors hover:brightness-125 disabled:opacity-60"
        style={{
          color: meta.text,
          backgroundColor: meta.bg,
          borderColor: `${meta.border}55`,
        }}
      >
        <Icon size={11} weight="fill" />
        {meta.label}
        {!disabled && <CaretDown size={9} className="opacity-60" />}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            data-testid={testId ? `${testId}-menu` : undefined}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: MENU_W,
              zIndex: 9999,
              backgroundColor: "#111111",
            }}
            className="border border-white/15 rounded-sm shadow-2xl shadow-black/50 py-1"
          >
            {Object.entries(STATUS_META).map(([key, m]) => {
              const KIcon = m.icon;
              return (
                <button
                  key={key}
                  onClick={(e) => {
                    e.stopPropagation();
                    pick(key);
                  }}
                  data-testid={testId ? `${testId}-opt-${key}` : undefined}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-white/5 transition-colors text-left"
                  style={{ color: m.text }}
                >
                  <KIcon size={11} weight="fill" />
                  <span className="flex-1">{m.label}</span>
                  {key === s && (
                    <CheckCircle size={11} weight="fill" className="opacity-60" />
                  )}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}

/**
 * Utility: returns the inline style for a table row tinted by status.
 */
export function rowTintStyle(status) {
  const meta = STATUS_META[normalizeStatus(status)];
  return {
    backgroundColor: meta.bg,
    borderLeft: `3px solid ${meta.border}`,
  };
}
